import axios from '../../axiosConfig';
import { createUserInContext } from '../users/userCreationApi';

const withTsParam = (params = {}) => ({
  ...params,
  ts: Date.now(),
});

export const findTempPassword = (responseData) => {
  const candidates = [
    responseData?.temp_password,
    responseData?.temporary_password,
    responseData?.generated_password,
    responseData?.password,
    responseData?.user?.temp_password,
    responseData?.user?.temporary_password,
    responseData?.user?.generated_password,
    responseData?.user?.password,
  ];

  return candidates.find((value) => typeof value === 'string' && value.trim()) || null;
};

export const parseCollection = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

const uniqueById = (items) =>
  items.filter((value, index, array) => {
    if (!value) return false;
    const key = value?.id != null ? `id:${value.id}` : `idx:${index}:${JSON.stringify(value)}`;
    return array.findIndex((item, itemIndex) => {
      if (!item) return false;
      const itemKey = item?.id != null ? `id:${item.id}` : `idx:${itemIndex}:${JSON.stringify(item)}`;
      return itemKey === key;
    }) === index;
  });

export const formatOrganizationLocation = (organization) => {
  const city = organization?.city?.trim();
  const country = organization?.country?.trim();
  if (city && country) return `${city}, ${country}`;
  return city || country || 'Location not set';
};

export const getConsultantOrganizations = async () => {
  const response = await axios.get('/consultant/organizations', { params: withTsParam() });
  return parseCollection(response?.data, ['organizations', 'consultant_organizations']);
};

const compactOrganizationPayload = (payload = {}) => {
  const organization = {};
  const stringFields = [
    'name',
    'contact',
    'title',
    'email',
    'phone',
    'address',
    'city',
    'state',
    'zip',
    'country',
    'notes',
    'billing_company_name',
    'billing_address',
    'billing_zip',
    'billing_city',
    'billing_state',
    'billing_country',
    'vat_or_tax_number',
    'currency',
    'billing_method',
    'billing_email',
    'billing_method_other',
    'reference_person',
    'reference_number',
    'cost_center',
    'billing_notes',
    'peppol',
    'gln',
    'organisationsnummer',
  ];

  stringFields.forEach((field) => {
    if (typeof payload?.[field] === 'string') {
      const trimmed = payload[field].trim();
      if (trimmed) {
        organization[field] = trimmed;
      }
    }
  });

  if (typeof payload?.billing_same_as_org === 'boolean') {
    organization.billing_same_as_org = payload.billing_same_as_org;
  }

  if (typeof payload?.postcode === 'string' && !organization.zip) {
    const trimmedPostcode = payload.postcode.trim();
    if (trimmedPostcode) {
      organization.zip = trimmedPostcode;
    }
  }

  return { organization };
};

export const createConsultantOrganization = async (payload) => {
  return axios.post('/consultant/organizations', compactOrganizationPayload(payload));
};

export const getConsultantOrganization = async (id) => {
  const response = await axios.get(`/organizations/${id}`, { params: withTsParam() });
  return response?.data?.organization || response?.data;
};

export const updateConsultantOrganization = async (id, payload) => {
  return axios.patch(`/organizations/${id}`, payload);
};

export const createConsultantOrganizationUser = async (organizationId, payload) => {
  return createUserInContext({ type: 'organization', id: organizationId, namespace: 'consultant' }, payload);
};

export const getConsultantDashboard = async () => {
  const response = await axios.get('/consultant/dashboard', { params: withTsParam() });
  return response?.data || {};
};

export async function getConsultantDashboardSummary() {
  const response = await axios.get('/consultant/dashboard_summary', { params: withTsParam() });
  return response?.data || {};
}

export const getCurrentConsultant = async () => {
  const payload = await getConsultantDashboardSummary();
  return payload?.consultant || null;
};

export const updateConsultant = async (id, payload) => {
  const response = await axios.patch(`/consultants/${id}`, { consultant: payload });
  return response?.data;
};

export const mapDashboardStats = (payload) => {
  const stats = payload?.stats || payload;
  return {
    organizations: Number(stats?.organizations ?? stats?.organizations_count ?? 0),
    orgUsers: Number(stats?.org_users ?? stats?.organization_users ?? stats?.org_users_count ?? 0),
    networks: Number(stats?.networks ?? stats?.networks_count ?? 0),
    networkOrgs: Number(stats?.network_orgs ?? stats?.network_organizations ?? stats?.network_orgs_count ?? 0),
    leads: Number(stats?.leads ?? stats?.leads_count ?? 0),
  };
};

export const mapRecentOrganizations = (payload) => {
  const recent =
    payload?.recent_organizations ||
    payload?.organizations ||
    payload?.recent ||
    [];

  return parseCollection(recent, ['organizations']).length > 0
    ? parseCollection(recent, ['organizations'])
    : Array.isArray(recent)
      ? recent
      : [];
};

export const getConsultantNetworks = async () => {
  const response = await axios.get('/consultant/networks', { params: withTsParam() });
  return parseCollection(response?.data, ['networks']);
};

export const createConsultantNetwork = async (payload) => {
  return axios.post('/consultant/networks', payload);
};

export const getConsultantNetwork = async (id) => {
  const response = await axios.get(`/consultant/networks/${id}`, { params: withTsParam() });
  const payload = response?.data || {};
  const network = payload?.network || payload?.consultant_network || null;

  return {
    network,
    organizations: uniqueById(parseCollection(payload, ['organizations'])),
    users: uniqueById(parseCollection(payload, ['users', 'network_users'])),
  };
};

export const updateConsultantNetwork = async (id, payload) => {
  return axios.patch(`/consultant/networks/${id}`, payload);
};

export const attachOrganizationToNetwork = async (networkId, organizationId) => {
  return axios.post(`/consultant/networks/${networkId}/organizations`, { organization_id: organizationId });
};

export const detachOrganizationFromNetwork = async (networkId, organizationId) => {
  return axios.delete(`/consultant/networks/${networkId}/organizations/${organizationId}`);
};

export const createNetworkUser = async (networkId, payload) => {
  return createUserInContext({ type: 'network', id: networkId, namespace: 'consultant' }, payload);
};
